/* MALU theme behaviour — vanilla port of the React components in src/.
   Every initializer is idempotent and scoped to a root, so the theme editor
   can re-run it when a section is added or re-rendered. */
(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = () => Boolean(navigator.connection && navigator.connection.saveData);
  const isDesktop = () => window.matchMedia('(min-width: 768px)').matches;
  const routes = () => (window.MALU && window.MALU.routes) || { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change' };
  const optionKey = (value) =>
    String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-');

  /** Runs `fn(el)` once per element matching `selector` inside `root`.
      Each initializer keeps its own marker, so two of them can share a selector. */
  const each = (root, selector, fn, marker) => {
    const nodes = root.matches && root.matches(selector) ? [root] : [];
    nodes.push(...root.querySelectorAll(selector));
    const key = "__maluInit_" + (marker || selector);
    nodes.forEach((el) => {
      if (el[key]) return;
      el[key] = true;
      fn(el);
    });
  };

  /* ---------------- Focus trap ---------------- */

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]';

  /** Traps Tab inside `container`, Escape calls `onClose`. Returns a release
      function that restores focus to where it was when the trap began. */
  function trapFocus(container, onClose) {
    const previouslyFocused = document.activeElement;
    const focusables = () =>
      Array.from(container.querySelectorAll(FOCUSABLE)).filter(
        // getClientRects, not offsetParent: fixed-position children (the zoom
        // overlay's close button) always report a null offsetParent
        (n) => n.getClientRects().length > 0 && !n.closest('[hidden]') && n.getAttribute('tabindex') !== '-1',
      );

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const nodes = focusables();
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.classList.add('no-scroll');
    // the open class makes the panel visible immediately, so focus can move in now
    focusables()[0]?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('no-scroll');
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus();
    };
  }

  /* ---------------- Reveal on scroll ---------------- */

  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-visible');
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -12% 0px', threshold: 0.05 },
        )
      : null;

  function initReveals(root) {
    each(root, '.reveal', (el) => {
      if (!revealObserver || reducedMotion() || window.Shopify?.designMode) {
        el.classList.add('is-visible');
      } else {
        revealObserver.observe(el);
      }
    });
  }

  /* ---------------- Videos: play only while in view ---------------- */

  function initVideos(root) {
    each(root, 'video[data-malu-video]', (video) => {
      if (reducedMotion() || saveData()) return;
      let inView = false;
      const update = () => {
        if (inView && !document.hidden && !video.hasAttribute('data-inactive')) {
          video.play().catch(() => {
            /* autoplay refused — the poster stays */
          });
        } else {
          video.pause();
        }
      };
      video.__maluUpdate = update;
      const threshold = parseFloat(video.dataset.threshold) || 0.3;
      new IntersectionObserver(
        (entries) => {
          inView = entries[0].isIntersecting;
          update();
        },
        { threshold },
      ).observe(video);
      document.addEventListener('visibilitychange', update);
    });
  }

  /* ---------------- Loader + hero entrance ---------------- */

  function initHero(root) {
    each(root, '[data-malu-hero]', (hero) => {
      const ready = () => hero.classList.add('hero--ready');
      const loader = document.querySelector('[data-malu-loader]');
      let seen = false;
      try {
        seen = sessionStorage.getItem('malu-loader-seen') === '1';
        sessionStorage.setItem('malu-loader-seen', '1');
      } catch {
        /* storage unavailable — show the loader */
      }
      if (!loader || seen || reducedMotion()) {
        loader?.remove();
        // let the start state paint first so the entrance transition runs
        // (a timeout, not rAF: rAF never fires in background tabs, leaving the headline hidden)
        setTimeout(ready, 50);
        return;
      }
      setTimeout(() => loader.classList.add('loader--leaving'), 880);
      setTimeout(() => {
        loader.remove();
        ready();
      }, 1250);
    });
  }

  /* ---------------- Header + mobile menu ---------------- */

  function initHeader(root) {
    each(root, '[data-malu-header]', (header) => {
      const onScroll = () => header.classList.toggle('site-header--scrolled', window.scrollY > 20);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    });

    each(root, '[data-malu-menu]', (menu) => {
      const panel = menu.querySelector('[data-malu-trap]');
      const openers = document.querySelectorAll('[data-malu-menu-open]');
      let release = null;

      const close = () => {
        if (!release) return;
        menu.classList.remove('mobile-menu--open');
        menu.setAttribute('aria-hidden', 'true');
        openers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
        const r = release;
        release = null;
        r();
      };
      const open = () => {
        if (release) return;
        menu.classList.add('mobile-menu--open');
        menu.setAttribute('aria-hidden', 'false');
        openers.forEach((b) => b.setAttribute('aria-expanded', 'true'));
        release = trapFocus(panel, close);
      };

      openers.forEach((b) => b.addEventListener('click', open));
      menu.querySelectorAll('[data-malu-menu-close]').forEach((el) => el.addEventListener('click', close));
    });
  }

  /* ---------------- Bag (cart drawer) ----------------
     Named cart-drawer and exposing Dawn's drawer interface so Dawn's own
     product form and quick-add open this bag on product pages too. */

  class MaluCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;
      this.release = null;
      this.addEventListener('click', (e) => {
        const closer = e.target.closest('[data-malu-cart-close]');
        if (closer) {
          this.close();
          return;
        }
        const line = e.target.closest('[data-malu-line]');
        if (line) this.changeLine(line.dataset.maluLine, Number(line.dataset.qty), line);
      });
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-malu-cart-open]');
        if (!opener) return;
        e.preventDefault();
        this.open();
      });
      // Dawn pages: keep the bag in step with the cart page and other widgets
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source !== 'malu') this.refresh();
        });
      }
    }

    get sectionId() {
      return 'malu-cart-drawer';
    }

    /* Dawn interface */
    getSectionsToRender() {
      return [{ id: this.sectionId, section: this.sectionId, selector: '[data-malu-cart-contents]' }];
    }
    renderContents(parsed) {
      if (parsed && parsed.sections && parsed.sections[this.sectionId]) this.render(parsed.sections[this.sectionId]);
      else this.refresh();
      this.open();
    }
    setActiveElement(element) {
      this.activeElement = element;
    }

    open() {
      if (this.release) return;
      this.classList.add('cart--open');
      this.setAttribute('aria-hidden', 'false');
      this.release = trapFocus(this.querySelector('[data-malu-trap]'), () => this.close());
    }

    close() {
      if (!this.release) return;
      this.classList.remove('cart--open');
      this.setAttribute('aria-hidden', 'true');
      const r = this.release;
      this.release = null;
      r();
    }

    /** Swaps in freshly rendered contents; keeps focus on the equivalent control. */
    render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('cart-drawer');
      if (!next) return;
      const focusedLabel = this.contains(document.activeElement) ? document.activeElement.getAttribute('aria-label') : null;

      this.querySelector('[data-malu-cart-contents]').innerHTML = next.querySelector('[data-malu-cart-contents]').innerHTML;
      updateCounts(Number(next.dataset.count));

      if (focusedLabel) {
        const same = Array.from(this.querySelectorAll('[aria-label]')).find((n) => n.getAttribute('aria-label') === focusedLabel);
        (same || this.querySelector('.cart__close')).focus();
      }
    }

    async refresh() {
      const res = await fetch(`${routes().root}?sections=${this.sectionId}`);
      const json = await res.json();
      this.render(json[this.sectionId]);
    }

    showError(message) {
      const el = this.querySelector('[data-malu-cart-error]');
      if (!el) return;
      el.textContent = message;
      el.hidden = false;
    }

    async changeLine(line, quantity, control) {
      control.disabled = true;
      try {
        const res = await fetch(`${routes().cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity, sections: [this.sectionId] }),
        });
        const json = await res.json();
        if (!res.ok || json.status) {
          control.disabled = false;
          this.showError(json.description || json.message || 'That quantity is not available.');
          return;
        }
        this.render(json.sections[this.sectionId]);
        publishCartUpdate(json);
      } catch {
        control.disabled = false;
        this.showError('Something went wrong. Please try again.');
      }
    }

    /** Adds `quantity` of `variantId`; resolves true, or rejects with Shopify's message. */
    async add(variantId, quantity = 1) {
      const res = await fetch(`${routes().cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity }], sections: [this.sectionId] }),
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || 'Unavailable');
      this.render(json.sections[this.sectionId]);
      publishCartUpdate(json);
      return true;
    }
  }

  function updateCounts(count) {
    if (Number.isNaN(count)) return;
    document.querySelectorAll('[data-malu-cart-count]').forEach((el) => {
      el.textContent = count;
    });
    document.querySelectorAll('[data-malu-cart-open]').forEach((el) => {
      el.setAttribute('aria-label', `Open bag, ${count} item${count === 1 ? '' : 's'}`);
    });
  }

  function publishCartUpdate(cartData) {
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'malu', cartData });
    }
  }

  const cart = () => document.querySelector('cart-drawer');

  /* ---------------- Lifestyle rail: drag + Quick Add ---------------- */

  function initRail(root) {
    each(root, '[data-malu-rail]', (el) => {
      const drag = { active: false, startX: 0, startScroll: 0, moved: false };
      el.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.target.closest('button')) return;
        Object.assign(drag, { active: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false });
      });
      el.addEventListener('pointermove', (e) => {
        if (!drag.active) return;
        const dx = e.clientX - drag.startX;
        if (Math.abs(dx) > 6 && !drag.moved) {
          drag.moved = true;
          el.classList.add('is-dragging');
          el.setPointerCapture?.(e.pointerId);
        }
        if (drag.moved) el.scrollLeft = drag.startScroll - dx;
      });
      const end = () => {
        drag.active = false;
        el.classList.remove('is-dragging');
      };
      el.addEventListener('pointerup', end);
      el.addEventListener('pointerleave', end);
      // swallow the click that ends a drag so links don't fire
      el.addEventListener(
        'click',
        (e) => {
          if (!drag.moved) return;
          e.preventDefault();
          e.stopPropagation();
          drag.moved = false;
        },
        true,
      );
    });

    each(root, '[data-malu-quickadd]', (wrap) => {
      const toggle = wrap.querySelector('[data-malu-quick-toggle]');
      const panel = wrap.querySelector('[data-malu-quick-panel]');
      const main = wrap.querySelector('.product-card__quickadd');
      let timer = 0;

      const setOpen = (open) => {
        if (!toggle) return;
        panel.hidden = !open;
        toggle.hidden = open;
        toggle.setAttribute('aria-expanded', String(open));
        wrap.classList.toggle('is-open', open);
        if (open) {
          document.querySelectorAll('[data-malu-quickadd].is-open').forEach((other) => {
            if (other !== wrap) other.__maluQuickClose?.();
          });
          panel.querySelector('[data-malu-quick-value][aria-pressed="true"], button:not([disabled])')?.focus();
        }
      };
      wrap.__maluQuickClose = () => setOpen(false);

      const setState = (button, state, label) => {
        button.classList.remove('product-card__quickadd--idle', 'product-card__quickadd--loading', 'product-card__quickadd--added', 'product-card__quickadd--unavailable');
        button.classList.add(`product-card__quickadd--${state}`);
        if (label !== undefined) button.innerHTML = label;
      };

      const add = async (variantId, button) => {
        const original = button.innerHTML;
        const target = toggle ? main : button;
        clearTimeout(timer);
        if (toggle) setOpen(false);
        setState(target, 'loading', '<span class="product-card__spinner" aria-hidden="true"></span>Adding…');
        try {
          await cart().add(variantId);
          setState(target, 'added', 'Added ✓');
          timer = setTimeout(() => {
            setState(target, 'idle', toggle ? 'Quick add +' : original);
            cart().open();
          }, 700);
        } catch {
          setState(target, 'unavailable', 'Unavailable');
          timer = setTimeout(() => setState(target, 'idle', toggle ? 'Quick add +' : original), 2000);
        }
      };

      if (toggle) {
        const variants = JSON.parse(wrap.querySelector('[data-malu-quick-variants]').textContent);
        const groups = Array.from(panel.querySelectorAll('[data-malu-quick-option]'));
        const fixed = Array.from(panel.querySelectorAll('[data-malu-quick-fixed]'));
        const submit = panel.querySelector('[data-malu-quick-submit]');
        const status = panel.querySelector('[data-malu-quick-status]');
        const optionCount = variants[0]?.options.length || 0;
        const resolveFixed = (input) => {
          const index = Number(input.dataset.optionIndex);
          const fixedValue = input.dataset.maluQuickFixed;
          input.dataset.maluQuickResolved =
            variants.flatMap((variant) => variant.options[index]).find((value) => optionKey(value) === optionKey(fixedValue)) || fixedValue;
        };
        fixed.forEach(resolveFixed);

        const selected = () => {
          const values = Array(optionCount).fill(null);
          fixed.forEach((input) => (values[Number(input.dataset.optionIndex)] = input.dataset.maluQuickResolved));
          groups.forEach((group) => {
            values[Number(group.dataset.optionIndex)] = group.querySelector('[aria-pressed="true"]')?.dataset.maluQuickValue;
          });
          return values;
        };

        const selectValue = (group, button) => {
          group.querySelectorAll('[data-malu-quick-value]').forEach((choice) => {
            choice.setAttribute('aria-pressed', String(choice === button));
          });
        };

        const sync = () => {
          const orderedGroups = groups
            .slice()
            .sort((a, b) => Number(b.dataset.optionColour !== undefined) - Number(a.dataset.optionColour !== undefined));
          const values = selected();
          const context = Array(optionCount).fill(null);
          fixed.forEach((input) => (context[Number(input.dataset.optionIndex)] = input.dataset.maluQuickResolved));

          orderedGroups.forEach((group) => {
            const index = Number(group.dataset.optionIndex);
            const buttons = Array.from(group.querySelectorAll('[data-malu-quick-value]'));
            buttons.forEach((button) => {
              const available = variants.some(
                (variant) =>
                  variant.available &&
                  variant.options[index] === button.dataset.maluQuickValue &&
                  context.every((value, optionIndex) => !value || optionIndex === index || variant.options[optionIndex] === value),
              );
              button.disabled = !available;
              if (!available && button.getAttribute('aria-pressed') === 'true') button.setAttribute('aria-pressed', 'false');
            });

            let chosen = group.querySelector('[data-malu-quick-value][aria-pressed="true"]:not(:disabled)');
            const enabled = buttons.filter((button) => !button.disabled);
            if (!chosen && (group.dataset.optionColour !== undefined || enabled.length === 1)) {
              chosen = enabled[0];
              if (chosen) selectValue(group, chosen);
            }
            values[index] = chosen?.dataset.maluQuickValue;
            context[index] = values[index];

            const valueLabel = group.querySelector('[data-malu-quick-option-value]');
            if (valueLabel) valueLabel.textContent = values[index] || '';
          });

          const variant = values.every(Boolean)
            ? variants.find((candidate) => candidate.options.every((option, index) => option === values[index]))
            : null;
          submit.dataset.variantId = variant?.id || '';
          submit.disabled = !variant?.available;
          if (status) status.textContent = variant?.available ? 'Ready to add' : 'Choose available options';
        };

        wrap.__maluQuickSetColour = (value) => {
          fixed.filter((input) => input.hasAttribute('data-malu-quick-external-colour')).forEach((input) => {
            input.dataset.maluQuickFixed = optionKey(value);
            resolveFixed(input);
          });
          sync();
        };

        groups.forEach((group) => {
          group.addEventListener('click', (event) => {
            const button = event.target.closest('[data-malu-quick-value]');
            if (!button || button.disabled) return;
            selectValue(group, button);
            sync();
          });
        });
        submit.addEventListener('click', () => {
          if (!submit.disabled && submit.dataset.variantId) add(submit.dataset.variantId, submit);
        });
        sync();
        toggle.addEventListener('click', () => {
          sync();
          if (!groups.length && !submit.disabled && submit.dataset.variantId) add(submit.dataset.variantId, toggle);
          else setOpen(true);
        });
        wrap.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !panel.hidden) {
            setOpen(false);
            toggle.focus();
          }
        });
        wrap.closest('.product-card')?.addEventListener('mouseleave', () => {
          if (!panel.hidden && !panel.contains(document.activeElement)) setOpen(false);
        });
      } else if (main) {
        main.addEventListener('click', () => {
          if (!main.classList.contains('product-card__quickadd--loading')) add(main.dataset.variantId, main);
        });
      }
    });
  }


  /* ---------------- All pieces: product previews ----------------
     Pointer and keyboard get a floating editorial preview; touch gets a
     disclosure under the row. Images are created on first use, so nothing
     is downloaded until someone asks for it. */

  function initPieces(root) {
    each(root, '.wanted__list', (list) => {
      const rows = Array.from(list.querySelectorAll('[data-malu-piece]'));
      if (!rows.length) return;
      const figure = list.querySelector('[data-malu-preview]');
      const fine = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      const cache = new Map();

      /** One <img> per row, built the first time it is needed. */
      const imageFor = (row) => {
        if (cache.has(row)) return cache.get(row);
        const img = new Image();
        img.src = row.dataset.previewSrc;
        img.srcset = row.dataset.previewSrcset;
        img.sizes = '280px';
        img.alt = '';
        img.decoding = 'async';
        img.className = 'wanted__preview-img';
        cache.set(row, img);
        return img;
      };

      /* ---- floating preview (mouse + keyboard) ---- */
      if (figure) {
        let current = null;
        let target = null;
        let shown = { x: 0, y: 0 };
        let frame = 0;

        const place = () => {
          frame = 0;
          if (!target) return;
          const ease = reducedMotion() ? 1 : 0.18;
          shown.x += (target.x - shown.x) * ease;
          shown.y += (target.y - shown.y) * ease;
          figure.style.transform = `translate3d(${Math.round(shown.x)}px, ${Math.round(shown.y)}px, 0)`;
          if (Math.abs(target.x - shown.x) > 0.5 || Math.abs(target.y - shown.y) > 0.5) frame = requestAnimationFrame(place);
        };

        const moveTo = (x, y, snap) => {
          const box = list.getBoundingClientRect();
          const w = figure.offsetWidth || 260;
          const h = figure.offsetHeight || 340;
          // keep the preview inside the list, never over the price column
          target = {
            x: Math.min(Math.max(x + 28, 0), Math.max(0, box.width - w)),
            y: Math.min(Math.max(y - h / 2, 0), Math.max(0, box.height - h)),
          };
          if (snap) shown = { ...target };
          if (!frame) frame = requestAnimationFrame(place);
        };

        const show = (row) => {
          if (current === row) return;
          current = row;
          figure.replaceChildren(imageFor(row));
          figure.classList.add('is-visible');
        };
        const hide = () => {
          current = null;
          figure.classList.remove('is-visible');
        };

        list.addEventListener('pointermove', (e) => {
          if (e.pointerType !== 'mouse' || !fine()) return;
          const row = e.target.closest('[data-malu-piece]');
          const box = list.getBoundingClientRect();
          if (!row) return hide();
          show(row);
          // fixed column, vertical follow: never covers the name or price
          moveTo(box.width * 0.52, e.clientY - box.top, false);
        });
        list.addEventListener('pointerleave', hide);

        // keyboard: anchor beside the focused row instead of the cursor
        list.addEventListener('focusin', (e) => {
          if (!fine()) return;
          const row = e.target.closest('[data-malu-piece]');
          if (!row) return;
          const box = list.getBoundingClientRect();
          const r = row.getBoundingClientRect();
          show(row);
          moveTo(box.width * 0.52, r.top - box.top + r.height / 2, true);
        });
        list.addEventListener('focusout', (e) => {
          if (!list.contains(e.relatedTarget)) hide();
        });
      }

      /* ---- touch disclosure ---- */
      list.querySelectorAll('[data-malu-peek]').forEach((button) => {
        const row = button.closest('[data-malu-piece]');
        const panel = row.querySelector('[data-malu-peek-panel]');
        button.addEventListener('click', () => {
          const open = button.getAttribute('aria-expanded') === 'true';
          // one preview at a time
          list.querySelectorAll('[data-malu-peek][aria-expanded="true"]').forEach((other) => {
            other.setAttribute('aria-expanded', 'false');
            other.closest('[data-malu-piece]').querySelector('[data-malu-peek-panel]').hidden = true;
            other.closest('[data-malu-piece]').classList.remove('is-open');
          });
          if (open) return;
          const media = panel.querySelector('[data-malu-peek-media]');
          if (!media.firstChild) media.append(imageFor(row).cloneNode(true));
          panel.hidden = false;
          button.setAttribute('aria-expanded', 'true');
          row.classList.add('is-open');
        });
      });
    });
  }

  /* ---------------- Dress slider ---------------- */

  function initSlider(root) {
    each(root, '[data-malu-slider]', (section) => {
      const dots = Array.from(section.querySelectorAll('[data-malu-slide-to]'));
      const mediaSets = Array.from(section.querySelectorAll('[data-malu-featured-media]'));
      const panels = (sel) => Array.from(section.querySelectorAll(sel));
      const count = dots.length;
      if (!count) return;
      let active = 0;

      const selectColour = (index, value) => {
        if (!value) return;
        const detail = section.querySelector(`.dress-slide[data-slide="${index}"]`);
        const actions = section.querySelector(`.dress-nav__actions[data-slide="${index}"]`);
        const mediaSet = mediaSets[index];
        detail?.querySelectorAll('[data-malu-featured-colour]').forEach((button) => {
          button.setAttribute('aria-pressed', String(optionKey(button.dataset.maluFeaturedColour) === optionKey(value)));
        });

        const quick = actions?.querySelector('[data-malu-quickadd]');
        quick?.__maluQuickSetColour?.(value);
        const fixed = quick?.querySelector('[data-malu-quick-external-colour]');
        const variantsNode = quick?.querySelector('[data-malu-quick-variants]');
        const variants = variantsNode ? JSON.parse(variantsNode.textContent) : [];
        const colourIndex = Number(fixed?.dataset.optionIndex);
        const selectedColour = fixed?.dataset.maluQuickResolved || value;
        const variant = variants.find(
          (candidate) => candidate.available && optionKey(candidate.options[colourIndex]) === optionKey(selectedColour),
        );
        const link = actions?.querySelector('[data-malu-featured-link]');
        if (link && quick?.dataset.productUrl && variant) link.href = `${quick.dataset.productUrl}?variant=${variant.id}`;
        if (detail) detail.dataset.variantId = variant?.id || '';

        if (!mediaSet) return;
        const colourMedia = Array.from(mediaSet.querySelectorAll('[data-malu-featured-colour-media]')).find(
          (item) => optionKey(item.dataset.maluFeaturedColourMedia) === optionKey(selectedColour),
        );
        const target = colourMedia || mediaSet.querySelector('.featured__slide--fallback');
        mediaSet.querySelectorAll('.featured__slide').forEach((item) => {
          const on = item === target;
          item.classList.toggle('featured__slide--active', on);
          item.setAttribute('aria-hidden', String(!on));
          if (item.matches('video')) {
            item.toggleAttribute('data-inactive', !on || index !== active);
            item.__maluUpdate?.();
          }
        });
      };

      const go = (next, focus) => {
        active = (next + count) % count;
        section.querySelectorAll('[data-malu-quickadd].is-open').forEach((quick) => quick.__maluQuickClose?.());
        dots.forEach((d, i) => {
          const on = i === active;
          d.setAttribute('aria-selected', String(on));
          d.tabIndex = on ? 0 : -1;
          d.classList.toggle('dress-nav__dot--active', on);
        });
        panels('.dress-slide[data-slide], .dress-nav__actions[data-slide]').forEach((p) => {
          p.hidden = Number(p.dataset.slide) !== active;
        });
        mediaSets.forEach((set, i) => {
          const on = i === active;
          set.classList.toggle('featured__media-set--active', on);
          set.setAttribute('aria-hidden', String(!on));
          if (on) {
            const selected = section.querySelector(`.dress-slide[data-slide="${i}"] [data-malu-featured-colour][aria-pressed="true"]`);
            selectColour(i, selected?.dataset.maluFeaturedColour);
          } else {
            set.querySelectorAll('video').forEach((video) => {
              video.setAttribute('data-inactive', '');
              video.__maluUpdate?.();
            });
          }
        });
        if (focus) dots[active].focus();
      };

      section.querySelectorAll('[data-malu-slide-step]').forEach((b) =>
        b.addEventListener('click', () => go(active + Number(b.dataset.maluSlideStep))),
      );
      dots.forEach((d) => d.addEventListener('click', () => go(Number(d.dataset.maluSlideTo))));
      section.querySelectorAll('[data-malu-featured-colour]').forEach((button) => {
        button.addEventListener('click', () => {
          const slide = button.closest('[data-slide]');
          selectColour(Number(slide.dataset.slide), button.dataset.maluFeaturedColour);
        });
      });
      section.querySelector('[role="tablist"]')?.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        go(active + (e.key === 'ArrowRight' ? 1 : -1), true);
      });

      // theme editor: selecting a dress block shows that slide
      section.addEventListener('shopify:block:select', (e) => {
        const slide = e.target.closest('[data-slide]');
        if (slide) go(Number(slide.dataset.slide));
      });
      go(0);
    });
  }

  /* ---------------- Product page ---------------- */

  const visibleMedia = (track) => Array.from(track?.children || []).filter((slide) => !slide.hidden);

  const mobileMedia = (track) =>
    visibleMedia(track).reduce((nearest, slide) => {
      if (!nearest) return slide;
      const left = Math.abs(slide.offsetLeft - track.offsetLeft - track.scrollLeft);
      const nearestLeft = Math.abs(nearest.offsetLeft - track.offsetLeft - track.scrollLeft);
      return left < nearestLeft ? slide : nearest;
    }, null);

  /** Shows one media item: swaps the stage slide on desktop, scrolls to it on mobile. */
  function selectMedia(section, mediaId) {
    const track = section.querySelector('[data-malu-gallery-track]');
    if (!track) return;
    const slide = track.querySelector(`[data-media-id="${mediaId}"]`);
    if (!slide) return;
    track.querySelectorAll('.pdp__slide').forEach((s) => s.classList.toggle('is-active', s === slide));
    if (!isDesktop()) {
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
    section.querySelectorAll('[data-malu-thumb]').forEach((t) => {
      const on = t.dataset.maluThumb === String(mediaId);
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-pressed', String(on));
      if (on) t.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
    });
    const index = visibleMedia(track).indexOf(slide);
    const counter = section.querySelector('[data-malu-media-index]');
    if (counter && index >= 0) counter.textContent = pad(index + 1);
  }

  /* ---------------- Product gallery: thumbnails, zoom, save ---------------- */

  function initProductMedia(root) {
    each(root, '[data-malu-product]', (section) => {
      const track = section.querySelector('[data-malu-gallery-track]');

      section.querySelectorAll('[data-malu-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => selectMedia(section, thumb.dataset.maluThumb));
      });

      section.querySelector('[data-malu-thumb-more]')?.addEventListener('click', () => {
        const rail = section.querySelector('[data-malu-thumb-track]');
        rail?.scrollBy({ top: rail.clientHeight * 0.8, behavior: reducedMotion() ? 'auto' : 'smooth' });
      });

      // mobile: keep the counter and thumbnails in step with the swipe position
      track?.addEventListener(
        'scroll',
        () => {
          if (isDesktop()) return;
          const slide = mobileMedia(track);
          const i = visibleMedia(track).indexOf(slide);
          const counter = section.querySelector('[data-malu-media-index]');
          if (counter && i >= 0) counter.textContent = pad(i + 1);
          section.querySelectorAll('[data-malu-thumb]').forEach((t) => {
            const on = slide && t.dataset.maluThumb === slide.dataset.mediaId;
            t.classList.toggle('is-active', Boolean(on));
            t.setAttribute('aria-pressed', String(Boolean(on)));
          });
        },
        { passive: true },
      );

      /* zoom overlay */
      const overlay = section.querySelector('[data-malu-zoom-overlay]');
      const figure = section.querySelector('[data-malu-zoom-figure]');
      let release = null;
      const close = () => {
        if (!release) return;
        overlay.hidden = true;
        const r = release;
        release = null;
        r();
      };
      section.querySelector('[data-malu-zoom]')?.addEventListener('click', () => {
        const active = isDesktop()
          ? track.querySelector('.pdp__slide.is-active img')
          : mobileMedia(track)?.querySelector('img');
        if (!active) return;
        const img = new Image();
        img.src = active.dataset.zoomSrc || active.currentSrc || active.src;
        img.alt = active.alt || '';
        figure.replaceChildren(img);
        overlay.hidden = false;
        release = trapFocus(overlay, close);
      });
      overlay?.addEventListener('click', (e) => {
        if (e.target.closest('[data-malu-zoom-close]') || e.target === overlay) close();
      });
    }, 'product-media');

    /* save for later (this browser only) */
    each(root, '[data-malu-save]', (button) => {
      const key = 'malu-saved';
      const read = () => {
        try {
          return JSON.parse(localStorage.getItem(key)) || [];
        } catch {
          return [];
        }
      };
      const id = button.dataset.maluSave;
      const sync = (list) => button.setAttribute('aria-pressed', String(list.includes(id)));
      sync(read());
      button.addEventListener('click', () => {
        const list = read();
        const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {
          /* private mode: the button still reflects this page view */
        }
        sync(next);
      });
    });
  }

  /* ---------------- Product detail tabs ---------------- */

  function initProductDetails(root) {
    each(root, '[data-malu-pdetails]', (section) => {
      const tabs = Array.from(section.querySelectorAll('[data-malu-ptab]'));
      const panels = Array.from(section.querySelectorAll('[role="tabpanel"]'));
      if (!tabs.length) return;
      const select = (index, focus) => {
        const i = (index + tabs.length) % tabs.length;
        tabs.forEach((t, n) => {
          const on = n === i;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
        });
        panels.forEach((p, n) => (p.hidden = n !== i));
        if (focus) tabs[i].focus();
      };
      tabs.forEach((t, i) => t.addEventListener('click', () => select(i)));
      section.querySelector('[role="tablist"]').addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const current = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
        select(current + (e.key === 'ArrowRight' ? 1 : -1), true);
      });
    }, 'product-details');
  }

  function initProduct(root) {
    each(root, '[data-malu-product]', (section) => {
      const form = section.querySelector('[data-malu-product-form]');
      if (!form) return;
      const variants = JSON.parse(form.querySelector('[data-malu-variants]').textContent);
      const idInput = form.querySelector('[data-malu-variant-id]');
      const add = form.querySelector('[data-malu-add]');
      const error = form.querySelector('[data-malu-form-error]');
      const qty = form.querySelector('[data-malu-qty-input]');
      const fieldsets = Array.from(form.querySelectorAll('.pdp__option'));
      const price = section.querySelector('[data-malu-price-current]');
      const compare = section.querySelector('[data-malu-price-compare]');
      const track = section.querySelector('[data-malu-gallery-track]');
      const colourFieldset = form.querySelector('[data-malu-colour-option]');

      const selected = () => fieldsets.map((fs) => fs.querySelector('input:checked')?.value);
      const findVariant = (opts) => variants.find((v) => v.options.every((o, i) => o === opts[i]));

      const setButton = (variant) => {
        let label = add.dataset.labelAdd;
        if (!variant) label = add.dataset.labelUnavailable;
        else if (!variant.available) label = add.dataset.labelSoldout;
        add.textContent = label;
        add.disabled = !variant || !variant.available;
      };

      // mark values that can't be bought with the other current selections
      const markAvailability = () => {
        const current = selected();
        fieldsets.forEach((fs, index) => {
          fs.querySelectorAll('input').forEach((input) => {
            const opts = current.slice();
            opts[index] = input.value;
            const v = findVariant(opts);
            input.nextElementSibling.classList.toggle('is-unavailable', !v || !v.available);
          });
        });
      };

      const showMedia = (mediaId) => {
        if (!mediaId || !track) return;
        selectMedia(section, mediaId);
      };

      const filterMedia = (colour, preferredMediaId) => {
        if (!track || !colour) {
          showMedia(preferredMediaId);
          return;
        }
        const normalisedColour = colour.trim().toLocaleLowerCase();
        const slides = Array.from(track.children);
        const hasGroup = slides.some(
          (slide) => slide.dataset.maluMediaColour?.trim().toLocaleLowerCase() === normalisedColour,
        );
        slides.forEach((slide) => {
          slide.hidden = hasGroup && slide.dataset.maluMediaColour?.trim().toLocaleLowerCase() !== normalisedColour;
        });
        section.querySelectorAll('[data-malu-thumb]').forEach((thumb) => {
          thumb.hidden = hasGroup && thumb.dataset.maluMediaColour?.trim().toLocaleLowerCase() !== normalisedColour;
        });

        const visible = visibleMedia(track);
        const total = section.querySelector('[data-malu-media-total]');
        if (total) total.textContent = pad(visible.length);
        const more = section.querySelector('[data-malu-thumb-more]');
        if (more) more.hidden = visible.length <= 4;
        section.querySelectorAll('[data-malu-thumb]:not([hidden])').forEach((thumb, index) => {
          thumb.setAttribute('aria-label', `Show image ${index + 1} of ${visible.length}`);
        });

        const preferred = visible.find((slide) => slide.dataset.mediaId === String(preferredMediaId));
        const active = visible.find((slide) => slide.classList.contains('is-active'));
        const target = preferred || active || visible[0];
        if (target) selectMedia(section, target.dataset.mediaId);
      };

      const update = () => {
        const opts = selected();
        fieldsets.forEach((fs, i) => {
          const label = fs.querySelector('[data-malu-option-value]');
          if (label) label.textContent = opts[i];
        });
        const variant = findVariant(opts);
        error.hidden = true;
        setButton(variant);
        markAvailability();
        filterMedia(colourFieldset?.querySelector('input:checked')?.value, variant?.media);
        if (!variant) return;
        idInput.value = variant.id;
        price.textContent = variant.price;
        compare.hidden = !variant.compare;
        if (variant.compare) compare.textContent = variant.compare;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      };

      fieldsets.forEach((fs) => fs.addEventListener('change', update));
      markAvailability();
      const initialVariant = findVariant(selected());
      if (initialVariant) idInput.value = initialVariant.id;
      filterMedia(colourFieldset?.querySelector('input:checked')?.value, initialVariant?.media);

      form.querySelectorAll('[data-malu-qty]').forEach((b) =>
        b.addEventListener('click', () => {
          qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) + Number(b.dataset.maluQty));
        }),
      );

      form.addEventListener('submit', async (e) => {
        if (!cart()) return; // no bag on the page: normal form post to /cart/add
        e.preventDefault();
        if (add.disabled) return;
        const label = add.textContent;
        add.disabled = true;
        add.textContent = 'Adding…';
        error.hidden = true;
        try {
          await cart().add(idInput.value, Math.max(1, parseInt(qty.value, 10) || 1));
          cart().open();
        } catch (err) {
          error.textContent = err.message || 'This item could not be added.';
          error.hidden = false;
        }
        add.textContent = label;
        add.disabled = false;
      });
    });

    // mobile gallery counter
    each(root, '[data-malu-gallery]', (gallery) => {
      const track = gallery.querySelector('[data-malu-gallery-track]');
      const index = gallery.querySelector('[data-malu-media-index]');
      if (!track || !index) return;
      track.addEventListener(
        'scroll',
        () => {
          const slides = visibleMedia(track);
          const i = slides.indexOf(mobileMedia(track));
          if (i >= 0) index.textContent = pad(i + 1);
        },
        { passive: true },
      );
    });
  }

  /* ---------------- Product recommendations ---------------- */

  function initRecommendations(root) {
    each(root, '[data-malu-recommendations]', async (section) => {
      try {
        const html = await (await fetch(section.dataset.url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const list = doc.querySelector('[data-malu-recommendations-list][data-performed]');
        // keep the collection fallback unless Shopify returned real recommendations
        if (list && list.children.length) {
          const target = section.querySelector('[data-malu-recommendations-list]');
          target.innerHTML = list.innerHTML;
          initAll(target);
        }
      } catch {
        /* the fallback grid stays */
      }
    });
  }

  /* ---------------- Shop: filters, sort, pagination in place ---------------- */

  function initCollection(root) {
    each(root, '[data-malu-collection]', (section) => {
      const sectionId = section.dataset.sectionId;

      const render = async (url, push = true) => {
        section.classList.add('is-loading');
        try {
          const target = new URL(url, window.location.origin);
          target.searchParams.set('section_id', sectionId);
          const html = await (await fetch(target)).text();
          const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-malu-collection]');
          if (!next) throw new Error('no section');
          const panelOpen = !section.querySelector('[data-malu-filter-panel]')?.hidden;
          section.innerHTML = next.innerHTML;
          // content is already on screen: skip the entrance reveal on re-render
          section.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
          if (panelOpen) {
            section.querySelector('[data-malu-filter-panel]')?.removeAttribute('hidden');
            section.querySelector('[data-malu-filter-toggle]')?.setAttribute('aria-expanded', 'true');
          }
          target.searchParams.delete('section_id');
          if (push) window.history.pushState({ maluCollection: true }, '', target);
          bind();
          initAll(section);
        } catch {
          window.location.href = url; // fall back to a normal page load
        }
        section.classList.remove('is-loading');
      };

      const formUrl = (form) => {
        const params = new URLSearchParams();
        new FormData(form).forEach((value, key) => {
          if (String(value).trim() !== '') params.append(key, value);
        });
        return `${form.action}?${params}`;
      };

      // listeners live on elements that are replaced on each render, so rebind
      const bind = () => {
        const form = section.querySelector('[data-malu-filter-form]');
        const toggle = section.querySelector('[data-malu-filter-toggle]');
        const panel = section.querySelector('[data-malu-filter-panel]');
        toggle?.addEventListener('click', () => {
          const open = panel.hidden;
          panel.hidden = !open;
          toggle.setAttribute('aria-expanded', String(open));
        });
        form?.addEventListener('change', (e) => {
          if (e.target.matches('[data-malu-autosubmit]')) render(formUrl(form));
        });
        form?.addEventListener('submit', (e) => {
          e.preventDefault();
          render(formUrl(form));
        });
      };

      section.addEventListener('click', (e) => {
        const link = e.target.closest('a.shop__chip, a.shop__clear, a.shop__page');
        if (!link) return;
        e.preventDefault();
        render(link.href).then(() => {
          if (link.classList.contains('shop__page')) section.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth' });
        });
      });
      window.addEventListener('popstate', (e) => {
        if (e.state?.maluCollection || section.isConnected) window.location.reload();
      });
      bind();
    });
  }

  /* ---------------- Tabs (customer stories) ---------------- */

  function initTabs(root) {
    each(root, '[data-malu-tabs]', (section) => {
      const tabs = Array.from(section.querySelectorAll('[data-malu-tab]'));
      const panels = Array.from(section.querySelectorAll('[role="tabpanel"]'));
      let active = 0;
      const select = (i, focus) => {
        active = (i + tabs.length) % tabs.length;
        tabs.forEach((t, n) => {
          const on = n === active;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          t.classList.toggle('press__tab--active', on);
        });
        panels.forEach((p, n) => (p.hidden = n !== active));
        if (focus) tabs[active].focus();
      };
      tabs.forEach((t, i) => t.addEventListener('click', () => select(i)));
      section.querySelector('[role="tablist"]')?.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        select(active + (e.key === 'ArrowRight' ? 1 : -1), true);
      });
    });
  }

  /* ---------------- Countdown ---------------- */

  const pad = (n) => String(n).padStart(2, '0');

  function initCountdown(root) {
    each(root, '[data-malu-countdown]', (section) => {
      const target = Number(section.dataset.target);
      const digits = {};
      section.querySelectorAll('[data-unit]').forEach((d) => (digits[d.dataset.unit] = d));

      const setExpired = () => {
        section.querySelectorAll('[data-when="upcoming"]').forEach((n) => (n.hidden = true));
        section.querySelectorAll('[data-when="live"]').forEach((n) => (n.hidden = false));
      };

      const tick = () => {
        const diff = target - Date.now();
        if (diff <= 0) {
          setExpired();
          clearInterval(section.__maluTimer);
          return;
        }
        const secs = Math.floor(diff / 1000);
        digits.days && (digits.days.textContent = pad(Math.floor(secs / 86400)));
        digits.hours && (digits.hours.textContent = pad(Math.floor((secs % 86400) / 3600)));
        digits.mins && (digits.mins.textContent = pad(Math.floor((secs % 3600) / 60)));
        digits.secs && (digits.secs.textContent = pad(secs % 60));
      };
      tick();
      section.__maluTimer = setInterval(tick, 1000);
    });

    each(root, '[data-malu-notify-open]', (button) => {
      button.addEventListener('click', () => {
        const form = button.parentElement.querySelector('[data-malu-email-form]');
        button.hidden = true;
        form.hidden = false;
        form.querySelector('input[type="email"]')?.focus();
      });
    });
  }

  /* ---------------- Email forms (Shopify customer form) ---------------- */

  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function initEmailForms(root) {
    each(root, 'form', (form) => {
      const input = form.querySelector('input[type="email"][name="contact[email]"]');
      const error = form.querySelector('[data-malu-email-error]');
      if (!input || !error) return;
      form.noValidate = true;
      const errorClass = Array.from(input.classList).find((c) => !c.endsWith('--error')) + '--error';

      form.addEventListener('submit', (e) => {
        if (!EMAIL.test(input.value.trim())) {
          e.preventDefault();
          error.hidden = false;
          input.classList.add(errorClass);
          input.focus();
          return;
        }
        const submit = form.querySelector('[type="submit"]');
        if (submit?.dataset.loadingLabel) {
          submit.textContent = submit.dataset.loadingLabel;
          submit.disabled = true;
        }
      });
      input.addEventListener('input', () => {
        error.hidden = true;
        input.classList.remove(errorClass);
      });
    });
  }

  /* ---------------- Contact form ---------------- */

  function initContactForm(root) {
    each(root, '[data-malu-contact]', (form) => {
      form.noValidate = true;
      const fields = [
        [form.querySelector('input[name="contact[email]"]'), (v) => EMAIL.test(v)],
        [form.querySelector('textarea[name="contact[body]"]'), (v) => v.length > 1],
      ].filter(([el]) => el);

      const setInvalid = (el, invalid) => {
        el.classList.toggle('is-invalid', invalid);
        el.setAttribute('aria-invalid', String(invalid));
        const error = el.parentElement.querySelector('[data-malu-field-error]');
        if (error) error.hidden = !invalid;
      };

      form.addEventListener('submit', (e) => {
        const invalid = fields.filter(([el, ok]) => !ok(el.value.trim()));
        fields.forEach(([el, ok]) => setInvalid(el, !ok(el.value.trim())));
        if (invalid.length) {
          e.preventDefault();
          invalid[0][0].focus();
          return;
        }
        const submit = form.querySelector('[type="submit"]');
        if (submit?.dataset.loadingLabel) {
          submit.textContent = submit.dataset.loadingLabel;
          submit.disabled = true;
        }
      });
      fields.forEach(([el]) => el.addEventListener('input', () => setInvalid(el, false)));
    });

    // after Shopify's post-and-reload, bring the success/error message into view
    each(root, '[data-malu-focus-on-load]', (el) => {
      el.scrollIntoView({ block: 'center' });
      el.focus({ preventScroll: true });
    });
  }

  /* ---------------- GSAP: lookbook pin + manifesto scrub ---------------- */

  const cleanups = new WeakMap();

  function initLookbook(root) {
    each(root, '[data-malu-lookbook]', (section) => {
      const track = section.querySelector('[data-malu-lookbook-track]');
      const progress = section.querySelector('[data-malu-lookbook-progress]');

      if (reducedMotion() || !window.gsap || !window.ScrollTrigger) {
        // swipeable rail instead of the pinned stage
        section.classList.add('lookbook--static');
        track.classList.add('lookbook__track--scroll');
        track.tabIndex = 0;
        return;
      }

      const { gsap, ScrollTrigger } = window;
      gsap.registerPlugin(ScrollTrigger);
      const travel = () => Math.max(0, track.scrollWidth - window.innerWidth);
      const ctx = gsap.context(() => {
        gsap.to(track, {
          x: () => -travel(),
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: () => `top ${getComputedStyle(document.documentElement).getPropertyValue('--header-h').trim() || '72px'}`,
            end: () => `+=${travel()}`,
            pin: true,
            scrub: 0.35,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            onUpdate: (self) => {
              progress.style.transform = `scaleX(${self.progress})`;
              // drives the decorative line drawing — same trigger, no second pin
              section.style.setProperty('--line-draw', String(1 - self.progress));
            },
          },
        });
      }, section);
      cleanups.set(section, () => {
        section.style.removeProperty('--line-draw');
        ctx.revert();
      });

      // re-measure once everything that affects the track width has loaded
      const refresh = () => ScrollTrigger.refresh();
      window.addEventListener('load', refresh, { once: true });
      document.fonts?.ready.then(refresh).catch(() => {});
    });
  }

  function initManifesto(root) {
    each(root, '[data-malu-manifesto]', (section) => {
      if (reducedMotion() || !window.gsap || !window.ScrollTrigger) {
        section.classList.add('manifesto--static');
        return;
      }
      const { gsap, ScrollTrigger } = window;
      gsap.registerPlugin(ScrollTrigger);
      const para = section.querySelector('[data-malu-manifesto-text]');
      const spans = Array.from(para.querySelectorAll('.manifesto__word'));
      const total = spans.length;
      const ctx = gsap.context(() => {
        ScrollTrigger.create({
          trigger: para,
          start: 'top 78%',
          end: 'bottom 42%',
          scrub: true,
          onUpdate: (self) => {
            const cutoff = self.progress * (total + 2);
            spans.forEach((span, i) => {
              const revealed = i < cutoff;
              span.classList.toggle('is-on', revealed);
              span.classList.toggle('is-hot', revealed && cutoff - i < 2.4);
            });
          },
        });
      }, section);
      cleanups.set(section, () => ctx.revert());
    });
  }

  /* ---------------- Misc ---------------- */

  function initMisc(root) {
    each(root, '[data-malu-top]', (button) => {
      button.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' }));
    });
  }

  /* ---------------- Boot ---------------- */

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', MaluCartDrawer);

  function initAll(root) {
    initReveals(root);
    initVideos(root);
    initHero(root);
    initHeader(root);
    initRail(root);
    initSlider(root);
    initPieces(root);
    initTabs(root);
    initProduct(root);
    initProductMedia(root);
    initProductDetails(root);
    initRecommendations(root);
    initCollection(root);
    initCountdown(root);
    initEmailForms(root);
    initContactForm(root);
    initLookbook(root);
    initManifesto(root);
    initMisc(root);
  }

  initAll(document);

  // theme editor: sections are re-rendered in place
  document.addEventListener('shopify:section:load', (e) => {
    initAll(e.target);
    window.ScrollTrigger?.refresh();
  });
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-malu-lookbook], [data-malu-manifesto]').forEach((s) => cleanups.get(s)?.());
    e.target.querySelectorAll('[data-malu-countdown]').forEach((s) => clearInterval(s.__maluTimer));
    requestAnimationFrame(() => window.ScrollTrigger?.refresh());
  });
})();
